import {
  Container,
  TextField,
  Button,
  Grid,
  Typography,
  TextareaAutosize,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isBetween from "dayjs/plugin/isBetween";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

// Extend dayjs with necessary plugins
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isBetween);

const stateOptions = [
  { value: "AL", label: "ALABAMA" },
  { value: "AK", label: "ALASKA" },
  { value: "AS", label: "AMERICAN SAMOA" },
  { value: "AZ", label: "ARIZONA" },
  { value: "AR", label: "ARKANSAS" },
  { value: "CA", label: "CALIFORNIA" },
  { value: "CO", label: "COLORADO" },
  { value: "CT", label: "CONNECTICUT" },
  { value: "DE", label: "DELAWARE" },
  { value: "DC", label: "DISTRICT OF COLUMBIA" },
  { value: "FL", label: "FLORIDA" },
  { value: "GA", label: "GEORGIA" },
  { value: "GU", label: "GUAM" },
  { value: "HI", label: "HAWAII" },
  { value: "ID", label: "IDAHO" },
  { value: "IL", label: "ILLINOIS" },
  { value: "IN", label: "INDIANA" },
  { value: "IA", label: "IOWA" },
  { value: "KS", label: "KANSAS" },
  { value: "KY", label: "KENTUCKY" },
  { value: "LA", label: "LOUISIANA" },
  { value: "ME", label: "MAINE" },
  { value: "MD", label: "MARYLAND" },
  { value: "MA", label: "MASSACHUSETTS" },
  { value: "MI", label: "MICHIGAN" },
  { value: "MN", label: "MINNESOTA" },
  { value: "MS", label: "MISSISSIPPI" },
  { value: "MO", label: "MISSOURI" },
  { value: "MT", label: "MONTANA" },
  { value: "NE", label: "NEBRASKA" },
  { value: "NV", label: "NEVADA" },
  { value: "NH", label: "NEW HAMPSHIRE" },
  { value: "NJ", label: "NEW JERSEY" },
  { value: "NM", label: "NEW MEXICO" },
  { value: "NY", label: "NEW YORK" },
  { value: "NC", label: "NORTH CAROLINA" },
  { value: "ND", label: "NORTH DAKOTA" },
  { value: "MP", label: "NORTHERN MARIANA IS" },
  { value: "OH", label: "OHIO" },
  { value: "OK", label: "OKLAHOMA" },
  { value: "OR", label: "OREGON" },
  { value: "PA", label: "PENNSYLVANIA" },
  { value: "PR", label: "PUERTO RICO" },
  { value: "PI", label: "RHODE ISLAND" },
  { value: "SC", label: "SOUTH CAROLINA" },
  { value: "SD", label: "SOUTH DAKOTA" },
  { value: "TN", label: "TENNESSEE" },
  { value: "TX", label: "TEXAS" },
  { value: "UT", label: "UTAH" },
  { value: "VT", label: "VERMONT" },
  { value: "VA", label: "VIRGINIA" },
  { value: "VI", label: "VIRGIN ISLANDS" },
  { value: "WA", label: "WASHINGTON" },
  { value: "WV", label: "WEST VIRGINIA" },
  { value: "WI", label: "WISCONSIN" },
  { value: "WY", label: "WYOMING" },
];

const providerList = [
  { value: "1", label: "Provider 1" },
  { value: "2", label: "Provider 2" },
  { value: "3", label: "Provider 3" },
];

// Custom styles for ReactSelect
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "0.875rem",
    marginTop: "8px",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "32px",
  }),
};

const Demographics = ({ patientId }) => {
  const token = localStorage.getItem("Thyra_Token");
  const auth = `Bearer ${token}`;
  const headers = {
    Authorization: auth,
    "Content-Type": "application/json",
  };

  // Initialize currentPracticeId with a parsed integer or fallback to 0
  const [currentPracticeId, setCurrentPracticeId] = useState(() => {
    const practiceId = localStorage.getItem("default_practice");
    return practiceId ? parseInt(practiceId, 10) : 0;
  });

  console.log("practice id", currentPracticeId);

  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [patientDOB, setPatientDOB] = useState("");
  const [dateError, setDateError] = useState("");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      id: 0,
      phoneNumber: "",
      firstName: "",
      lastName: "",
      dob: "",
      diagnosis1: "",
      diagnosis2: "",
      gender: "",
      emailAddress: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      practiceId: currentPracticeId || 0,
      providerId: 0,
      illnessDate: null,
      intakeDate: null,
      diagnosis: "",
      ethnicity: "",
      billingRenderingProvider: "",
      middlename: "",
      clientId: 0,
      assignedto: "",
      signature: "",
      typeOfPatient: "",
      race: "",
      hmo: false,
      ppo: false,
      medicareStatus: "",
      comments: "",
      ssn: "",
      stripeCustomerId: "",
      cardId: "",
      roomName: "",
    },
  });

  useEffect(() => {
    if (patientId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/Patient/getPatientById?Id=${patientId}`,
          { headers }
        )
        .then((response) => {
          const patientData = response; // Assuming response.data contains the patient data
          console.log("Fetched patient data:", patientData); // Log to verify response
          // Populate form fields with patient data
          setValue("id", patientData.id || 0);
          setValue("phoneNumber", patientData.phoneNumber || "");
          setValue("firstName", patientData.firstName || "");
          setValue("lastName", patientData.lastName || "");
          setValue("middlename", patientData.middlename || "");
          setValue("gender", patientData.gender || "");
          setValue("emailAddress", patientData.emailAddress || "");
          setValue("address", patientData.address || "");
          setValue("city", patientData.city || "");
          setValue("state", patientData.state || "");
          setValue("zipCode", patientData.zipCode || "");
          setValue("diagnosis1", patientData.diagnosis1 || "");
          setValue("diagnosis2", patientData.diagnosis2 || "");
          setValue("diagnosis", patientData.diagnosis || "");
          setValue(
            "billingRenderingProvider",
            patientData.billingRenderingProvider || ""
          );
          setValue("roomName", patientData.roomName || "");
          setValue("comments", patientData.comments || "");
          setValue("ssn", patientData.ssn || "");
          // Set practiceId from patient data or fallback to currentPracticeId
          setValue("practiceId", patientData.practiceId || currentPracticeId || 0);

          // Format and set DOB
          if (patientData.dob) {
            const formattedDOB = dayjs(patientData.dob).format("YYYY-MM-DD");
            setPatientDOB(formattedDOB);
            setValue("dob", formattedDOB);
          }
        })
        .catch((error) => {
          console.error("Error fetching patient data:", error);
          alert("Failed to fetch patient data.");
        });
    }
  }, [patientId, setValue, currentPracticeId]);

  const onSubmit = async (data) => {
    if (!data.dob) {
      setValue("dob", null, { shouldValidate: true });
      return;
    }

    setSubmitting(true);
    const postData = {
      ...data,
      dob: data.dob ? dayjs(data.dob).format("YYYY-MM-DD") : null,
      illnessDate: data.illnessDate === "null" ? null : data.illnessDate,
      intakeDate: data.intakeDate === "null" ? null : data.intakeDate,
      practiceId: data.practiceId || currentPracticeId || 0, // Use form's practiceId
    };

    const selectedProvider = providerList.find(
      (provider) => provider.label === data.billingRenderingProvider
    );
    postData.providerId = selectedProvider ? Number(selectedProvider.value) : 0;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/Patient/addPatient`,
        postData,
        { headers }
      );
      // alert("Patient saved successfully!");
      reset(); // Reset form after successful submission
      navigate("/Patient"); // Navigate back to Patient list
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to save patient. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    navigate("/Patient"); // Navigate back to Patient list
  };

  return (
    <React.Fragment>
      <Grid className="page-content">
        <Container>
          <Grid container spacing={2}>
            <Grid className="row">
              {/* Email Address */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Email Address
                </Typography>
                <TextField
                  {...register("emailAddress", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email address",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your email"
                  sx={{ mt: 1 }}
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress?.message}
                />
              </Grid>

              {/* Last Name */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Last Name
                </Typography>
                <TextField
                  {...register("lastName", {
                    required: "Last name is required",
                    pattern: {
                      value: /^[^,\s]+$/,
                      message: "Last name cannot contain spaces or commas",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your last name"
                  sx={{ mt: 1 }}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Grid>

              {/* First Name */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  First Name
                </Typography>
                <TextField
                  {...register("firstName", {
                    required: "First name is required",
                    pattern: {
                      value: /^[^,\s]+$/,
                      message: "First name cannot contain spaces or commas",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your first name"
                  sx={{ mt: 1 }}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </Grid>

              {/* Middle Name */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Middle Name
                </Typography>
                <TextField
                  {...register("middlename")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your middle name"
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* DOB */}
<Grid className="col-md-4">
  <Typography component="label" color="black">
    DOB
  </Typography> {/* This was incorrectly closed or corrupted in the original code */}
  <TextField
    type="date"
    fullWidth
    variant="outlined"
    size="small"
    placeholder="Enter your DOB"
    value={patientDOB}
    onChange={(e) => {
      const selectedDate = e.target.value;
      const currentDate = new Date().toISOString().split("T")[0];

      if (!selectedDate) {
        setDateError("DOB is required.");
        return;
      }

      if (selectedDate > currentDate) {
        setDateError(
          "Please enter a valid DOB. Future dates are not allowed."
        );
        return;
      }

      setDateError("");
      setPatientDOB(selectedDate);
      setValue("dob", selectedDate);
    }}
    error={!!dateError}
    helperText={dateError}
  />
</Grid>

              {/* Gender */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Gender
                </Typography>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "Gender is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={[
                        { value: "Male", label: "Male" },
                        { value: "Female", label: "Female" },
                        { value: "Other", label: "Other" },
                      ]}
                      value={
                        field.value
                          ? { label: field.value, value: field.value }
                          : null
                      }
                      onChange={(selected) =>
                        field.onChange(selected?.value || "")
                      }
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          fontSize: "16px",
                          borderColor: state.isFocused
                            ? "#1976d2"
                            : base.borderColor,
                          boxShadow: state.isFocused
                            ? "0 0 0 2px rgba(25, 118, 210, 0.2)"
                            : base.boxShadow,
                          "&:hover": {
                            borderColor: "#1976d2",
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: "16px",
                          padding: "10px 12px",
                          backgroundColor: state.isSelected
                            ? "#e0e0e0"
                            : state.isFocused
                            ? "#f5f5f5"
                            : "#fff",
                          color: "#000",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "16px",
                        }),
                      }}
                    />
                  )}
                />
                {errors.gender && (
                  <Typography color="error" sx={{ fontSize: "14px", mt: 0.5 }}>
                    {errors.gender.message}
                  </Typography>
                )}
              </Grid>

              {/* SSN */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  SSN
                </Typography>
                <TextField
                  {...register("ssn", {
                    required: "SSN is required",
                    pattern: {
                      value: /^\d{3}-\d{2}-\d{4}$/,
                      message: "Please enter a valid SSN (123-12-1234)",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="123-12-1234"
                  sx={{ mt: 1 }}
                  error={!!errors.ssn}
                  helperText={errors.ssn?.message}
                />
              </Grid>

              {/* Phone Number */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Phone Number
                </Typography>
                <TextField
                  {...register("phoneNumber", {
                    required: "Phone number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Please enter a valid 10-digit phone number",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter phone number"
                  sx={{ mt: 1 }}
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                  }}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                />
              </Grid>

              {/* Address */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Address
                </Typography>
                <TextField
                  {...register("address", {
                    required: "Address is required",
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your address"
                  sx={{ mt: 1 }}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              </Grid>

              {/* City */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  City
                </Typography>
                <TextField
                  {...register("city", { required: "City is required" })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your city"
                  sx={{ mt: 1 }}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>

              {/* State */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  State
                </Typography>
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: "State is required" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={stateOptions}
                      value={
                        field.value
                          ? stateOptions.find(
                              (opt) => opt.value === field.value
                            )
                          : null
                      }
                      onChange={(selected) =>
                        field.onChange(selected?.value || "")
                      }
                      styles={customSelectStyles}
                    />
                  )}
                />
                {errors.state && (
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.state.message}
                  </Typography>
                )}
              </Grid>

              {/* Zip Code */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Zip Code
                </Typography>
                <TextField
                  {...register("zipCode", {
                    required: "Zip code is required",
                    pattern: {
                      value: /^[0-9]{5}(?:-[0-9]{4})?$/,
                      message:
                        "Please enter a valid zip code (12345 or 12345-6789)",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter zip code"
                  sx={{ mt: 1 }}
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message}
                />
              </Grid>

              {/* Diagnosis 1 */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Diagnosis 1
                </Typography>
                <TextField
                  {...register("diagnosis")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter diagnosis 1"
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* Diagnosis 2 */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Diagnosis 2
                </Typography>
                <TextField
                  {...register("diagnosis1")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter diagnosis 2"
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* Diagnosis 3 */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Diagnosis 3
                </Typography>
                <TextField
                  {...register("diagnosis2")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter diagnosis 3"
                  sx={{ mt: 1 }}
                />
              </Grid>

              {/* Billing Rendering Provider */}
              <Grid className="col-md-4">
                <Typography component="label" color="black">
                  Billing Rendering Provider
                </Typography>
                <Controller
                  name="billingRenderingProvider"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={providerList}
                      value={
                        providerList.find((opt) => opt.label === field.value) ||
                        null
                      }
                      onChange={(selected) =>
                        field.onChange(selected?.label || "")
                      }
                      styles={customSelectStyles}
                    />
                  )}
                />
              </Grid>

              {/* Comments */}
              <Grid item xs={12}>
                <Typography component="label" color="black">
                  Comments
                </Typography>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <TextareaAutosize
                      {...field}
                      aria-label="minimum height"
                      minRows={3}
                      placeholder="Enter comments"
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        padding: "8px",
                        fontSize: "0.875rem",
                        resize: "vertical",
                      }}
                    />
                  )}
                />
              </Grid>
              
            </Grid>
          
          

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                width: "100px",
                height: "35px",
                backgroundColor: "#2B7B7E",
                color: "white",
                borderRadius: "80px",
                fontSize: "14px",
                "&:hover": { backgroundColor: "#1a1a1a" },
              }}
            >
              <CancelOutlinedIcon
                fontSize="medium"
                style={{ color: "red", paddingRight: "5px" }}
              />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={submitting}
              sx={{
                width: "100px",
                height: "35px",
                backgroundColor: "#2B7B7E",
                color: "white",
                borderRadius: "80px",
                fontSize: "14px",
                "&:hover": { backgroundColor: "#1a1a1a" },
              }}
            >
              <SaveOutlinedIcon sx={{ color: "white", mr: 1 }} />
              Save
            </Button>
          </Grid>
          </Grid>
        </Container>
      </Grid>
    </React.Fragment>
  );
};

export default Demographics;